import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FilterMenuConfig } from "@/shared/models/filterTypes";
import { useWidgetFilterState } from "@/hooks/useWidgetFilterState";
import { FilterPanelToggle } from "@/ui-components/FilterPanel";
import { SearchFiltersFooterProps } from "@/widget-components/newsquawk-headlines/SearchFiltersFooter";

export interface UseFilterPanelMenuReturn {
  /** Current filter selections */
  selections: Record<string, string[]>;
  /** Set filter selections */
  setSelections: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  /** Whether the panel is open */
  isPanelOpen: boolean;
  /** Toggle panel open/closed */
  togglePanel: () => void;
  /** Open the panel */
  openPanel: () => void;
  /** Close the panel */
  closePanel: () => void;
  /** Filter menu configurations */
  filterMenus: FilterMenuConfig[];
  /** Total count of active selections */
  selectionsCount: number;
  /** Clear all selections */
  clearSelections: () => void;
  /** Footer element with clearSelections injected */
  footerElement: React.ReactElement | null;
}

export function useFilterPanelMenu(
  filterConfig: FilterMenuConfig[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pushAction?: (action: React.ReactElement<any>) => void,
  persistKey?: string,
  icon?: React.ReactElement,
  footer?: React.ReactElement<SearchFiltersFooterProps>,
  /** Callback when panel opens - use to close other panels */
  onOpen?: () => void,
  /** Close the search panel when this panel opens */
  closeSearch?: () => void,
  /** Register to close this panel when search opens */
  onSearchOpen?: (callback: () => void) => () => void
): UseFilterPanelMenuReturn {
  const [selections, setSelections] = useWidgetFilterState(persistKey, "filter");

  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const openPanel = useCallback(() => {
    onOpen?.();
    closeSearch?.();
    setIsPanelOpen(true);
  }, [onOpen, closeSearch]);

  const togglePanel = useCallback(() => {
    if (!isPanelOpen) {
      onOpen?.();
      closeSearch?.();
    }
    setIsPanelOpen((prev) => !prev);
  }, [isPanelOpen, onOpen, closeSearch]);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  // Close this panel when search opens
  useEffect(() => {
    if (!onSearchOpen) return;
    return onSearchOpen(() => setIsPanelOpen(false));
  }, [onSearchOpen]);

  const clearSelections = useCallback(() => {
    setSelections({});
  }, [setSelections]);

  const selectionsCount = useMemo(
    () =>
      Object.entries(selections).reduce(
        (acc, [, items]) => acc + items.length,
        0
      ),
    [selections]
  );

  // Create footer element with clearSelections injected
  const footerElement = useMemo(() => {
    if (!footer) return null;

    return React.cloneElement(footer, {
      clearSelections,
      disableClear: selectionsCount === 0,
    });
  }, [footer, clearSelections, selectionsCount]);

  // Push toggle button to header actions
  useEffect(() => {
    if (!pushAction) return;

    const Toggle = (
      <FilterPanelToggle
        key="02-filter-toggle"
        isOpen={isPanelOpen}
        hasSelections={selectionsCount > 0}
        onToggle={togglePanel}
        icon={icon}
      />
    );

    pushAction(Toggle);
  }, [pushAction, persistKey, isPanelOpen, selectionsCount, togglePanel, icon]);

  return {
    selections,
    setSelections,
    isPanelOpen,
    togglePanel,
    openPanel,
    closePanel,
    filterMenus: filterConfig,
    selectionsCount,
    clearSelections,
    footerElement,
  };
}
